import InlineScript from '../components/InlineScript';
import { FormEvent, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { pageUrl } from '../config/site';
import {
  BEFORE_AFTER,
  INTERIOR_PAINTING_SHOTS,
  WORK_SHOTS,
} from '../constants/projectGallery';
import Navigation from '../components/navigation';
import Footer from '../components/footer';
import { submitToMailer } from '../lib/submitToMailer';
import './before-after-gallery.css';

export default function BeforeAfterGallery() {
  const [ctaMail, setCtaMail] = useState<{
    status: 'idle' | 'sending' | 'ok' | 'err';
    message: string;
  }>({ status: 'idle', message: '' });

  async function handleGalleryCtaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setCtaMail({ status: 'sending', message: '' });
    const form = e.currentTarget;
    const fd = new FormData(form);
    const result = await submitToMailer({
      form: 'contact',
      name: String(fd.get('name') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      email: String(fd.get('email') ?? ''),
      service: String(fd.get('service') ?? ''),
      message: String(fd.get('message') ?? ''),
      contactTime: 'anytime',
      freeEstimate: false,
      fileCount: 0,
    });
    if (result.ok) {
      setCtaMail({
        status: 'ok',
        message: 'Thanks — we received your request and will reply soon.',
      });
      form.reset();
    } else {
      setCtaMail({ status: 'err', message: result.error });
    }
  }

  return (
    <div className="before-after-gallery-container1">
      <Helmet>
        <title>Before &amp; After Gallery | Shiny Touch Painting</title>
        <meta
          property="og:title"
          content="Before &amp; After Gallery | Shiny Touch Painting"
        />
        <link rel="canonical" href={pageUrl('/before-after-gallery')} />
        <meta property="og:url" content={pageUrl('/before-after-gallery')} />
      </Helmet>
      <Navigation></Navigation>
      <section className="gallery-hero">
        <div id="heroCarousel" className="gallery-hero-carousel">
          <div className="gallery-hero-item active">
            <img
              src={BEFORE_AFTER.project0.after}
              alt="Completed painting and renovation project"
              className="gallery-hero-bg"
            />
            <div className="gallery-hero-overlay"></div>
            <div className="gallery-hero-content">
              <h1 className="before---after-gallery-hero-title hero-title">
                Transforming Spaces with Precision
              </h1>
              <p className="hero-subtitle">
                See the stunning transformation of our latest interior
                renovation projects.
              </p>
              <div className="gallery-hero-actions">
                <a href="#project-gallery">
                  <div className="btn btn-primary btn-lg">
                    <span>Explore Projects</span>
                  </div>
                </a>
                <a href="#quote-cta">
                  <div className="btn btn-lg btn-outline">
                    <span>Request a Quote</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
          <div className="gallery-hero-item">
            <img
              src={BEFORE_AFTER.project1.after}
              alt="Another completed Shiny Touch project"
              className="gallery-hero-bg"
            />
            <div className="gallery-hero-overlay"></div>
            <div className="gallery-hero-content">
              <h1 className="before---after-gallery-hero-title hero-title">
                Exterior Revivals That Last
              </h1>
              <p className="hero-subtitle">
                Premium exterior painting and strata maintenance across Burnaby.
              </p>
              <div className="gallery-hero-actions">
                <a href="#project-gallery">
                  <div className="btn btn-primary btn-lg">
                    <span>View Exterior Work</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="gallery-hero-nav">
          <button
            aria-label="Slide 1"
            className="gallery-hero-dot active"
          ></button>
          <button aria-label="Slide 2" className="gallery-hero-dot"></button>
        </div>
      </section>
      <section id="project-gallery" className="project-gallery">
        <div className="project-gallery-container">
          <div className="project-gallery-header">
            <h2 className="section-title">Our Transformation Portfolio</h2>
            <div className="project-gallery-filters">
              <button data-filter="all" className="filter-btn active">
                All Projects
              </button>
              <button data-filter="interior" className="filter-btn">
                Interior
              </button>
              <button data-filter="exterior" className="filter-btn">
                Exterior
              </button>
              <button data-filter="renovation" className="filter-btn">
                Renovation
              </button>
              <button data-filter="strata" className="filter-btn">
                Strata
              </button>
            </div>
          </div>
          <div className="project-gallery-grid">
            <div data-category="exterior" className="project-card">
              <div className="project-card-media">
                <div className="before-after-container">
                  <img
                    src={BEFORE_AFTER.project0.before}
                    alt="Exterior — before"
                    className="img-before"
                  />
                  <img
                    src={BEFORE_AFTER.project0.after}
                    alt="Exterior — after"
                    className="img-after"
                  />
                  <div className="comparison-slider"></div>
                </div>
                <span className="project-badge">Exterior Painting</span>
              </div>
              <div className="project-card-info">
                <h3 className="section-subtitle">Exterior transformation</h3>
              </div>
            </div>
            <div data-category="exterior" className="project-card">
              <div className="project-card-media">
                <div className="before-after-container">
                  <img
                    src={BEFORE_AFTER.project1.before}
                    alt="Exterior — before"
                    className="img-before"
                  />
                  <img
                    src={BEFORE_AFTER.project1.after}
                    alt="Exterior — after"
                    className="img-after"
                  />
                  <div className="comparison-slider"></div>
                </div>
                <span className="project-badge">Exterior Painting</span>
              </div>
              <div className="project-card-info">
                <h3 className="section-subtitle">
                  Exterior project — before &amp; after
                </h3>
              </div>
            </div>
            <div data-category="interior" className="project-card">
              <div className="project-card-media">
                <div className="before-after-container">
                  <img
                    src={INTERIOR_PAINTING_SHOTS.kitchenRenovation}
                    alt="Interior painting — kitchen and open living area"
                    className="img-before"
                  />
                  <img
                    src={INTERIOR_PAINTING_SHOTS.roomInProgress}
                    alt="Interior painting — room in progress"
                    className="img-after"
                  />
                  <div className="comparison-slider"></div>
                </div>
                <span className="project-badge">Interior Painting</span>
              </div>
              <div className="project-card-info">
                <h3 className="section-subtitle">
                  Professional interior painting
                </h3>
              </div>
            </div>
            <div data-category="renovation" className="project-card">
              <div className="project-card-media">
                <div className="before-after-container">
                  <img
                    src={WORK_SHOTS[0]}
                    alt="Earlier stage on site"
                    className="img-before"
                  />
                  <img
                    src={WORK_SHOTS[1]}
                    alt="Further along"
                    className="img-after"
                  />
                  <div className="comparison-slider"></div>
                </div>
                <span className="project-badge">Full Renovation</span>
              </div>
              <div className="project-card-info">
                <h3 className="section-subtitle">On-site progress</h3>
              </div>
            </div>
            <div data-category="strata" className="project-card">
              <div className="project-card-media">
                <div className="before-after-container">
                  <img
                    src={WORK_SHOTS[1]}
                    alt="Mid-project detail"
                    className="img-before"
                  />
                  <img
                    src={WORK_SHOTS[2]}
                    alt="Completed detail"
                    className="img-after"
                  />
                  <div className="comparison-slider"></div>
                </div>
                <span className="project-badge">Strata Maintenance</span>
              </div>
              <div className="project-card-info">
                <h3 className="section-subtitle">Finish quality</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="featured-showcase">
        <div id="featuredCarousel" className="featured-showcase-carousel">
          <div className="featured-item active">
            <div className="featured-grid">
              <div className="featured-media">
                <div className="before-after-container large">
                  <img
                    src={BEFORE_AFTER.project0.before}
                    alt="Before"
                    className="img-before"
                  />
                  <img
                    src={BEFORE_AFTER.project0.after}
                    alt="After"
                    className="img-after"
                  />
                  <div className="comparison-slider"></div>
                </div>
              </div>
              <div className="featured-content">
                <h2 className="section-title">The Heritage Estate Revival</h2>
                <p className="section-content">
                  A comprehensive interior restoration focusing on preserving
                  architectural details while modernizing the color palette.
                </p>
                <div className="featured-meta">
                  <div className="meta-item">
                    <span className="meta-label">Service:</span>
                    <span className="meta-value">
                      Interior Painting &amp; Restoration
                    </span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Key Result:</span>
                    <span className="meta-value">
                      40% Increase in Property Value
                    </span>
                  </div>
                </div>
                <a href="#quote-cta">
                  <div className="btn btn-primary">
                    <span>Start Your Transformation</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="process-timeline">
        <div className="process-container">
          <h2 className="section-title">Our Proven Workflow</h2>
          <div className="timeline-wrapper">
            <div className="timeline-track">
              <div className="timeline-step">
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                    <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <h3 className="section-subtitle">1. Consult</h3>
                  <p className="section-content">
                    Detailed site assessment and personalized color/material
                    selection.
                  </p>
                </div>
              </div>
              <div className="timeline-step">
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.622 17.897l-10.68-2.913M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0zM9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <h3 className="section-subtitle">2. Prep</h3>
                  <p className="section-content">
                    Surface repair, sanding, and meticulous protection of your
                    belongings.
                  </p>
                </div>
              </div>
              <div className="timeline-step">
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <g
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    >
                      <path d="m11 10l3 3m-7.5 8A3.5 3.5 0 1 0 3 17.5a2.62 2.62 0 0 1-.708 1.792A1 1 0 0 0 3 21z"></path>
                      <path d="M9.969 17.031L21.378 5.624a1 1 0 0 0-3.002-3.002L6.967 14.031"></path>
                    </g>
                  </svg>
                </div>
                <div className="step-content">
                  <h3 className="section-subtitle">3. Renovate</h3>
                  <p className="section-content">
                    Expert application of premium paints and high-quality
                    renovation work.
                  </p>
                </div>
              </div>
              <div className="timeline-step">
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <h3 className="section-subtitle">4. Inspect</h3>
                  <p className="section-content">
                    Final walkthrough and quality check to ensure 100%
                    satisfaction.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="video-walkthroughs">
        <div className="video-container">
          <h2 className="section-title">Experience the Detail</h2>
          <div className="video-rail">
            <div className="video-card">
              <img
                src={INTERIOR_PAINTING_SHOTS.kitchenRenovation}
                alt="Professional painters working on kitchen and open living area"
                className="video-player"
                loading="lazy"
              />
              <div className="video-info">
                <h3 className="section-subtitle">Kitchen &amp; living area</h3>
                <p className="section-content">
                  Full prep, protection, and roller work on walls and trim.
                </p>
              </div>
            </div>
            <div className="video-card">
              <img
                src={INTERIOR_PAINTING_SHOTS.roomInProgress}
                alt="Interior painting in progress with masked windows and protected floors"
                className="video-player"
                loading="lazy"
              />
              <div className="video-info">
                <h3 className="section-subtitle">Room painting in progress</h3>
                <p className="section-content">
                  Careful masking and clean job-site practices on every visit.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="client-testimonials">
        <div className="testimonials-container">
          <h2 className="section-title">What Our Clients Say</h2>
          <div className="testimonials-rail">
            <div className="testimonial-card">
              <div className="testimonial-project">
                <img src={WORK_SHOTS[3]} alt="Project thumbnail" />
              </div>
              <div className="testimonial-content">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="quote-icon"
                >
                  <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2zM5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path>
                </svg>
                <p className="section-content">
                  &quot;Shiny Touch transformed our outdated apartment into a
                  modern masterpiece. Their attention to detail is
                  unmatched!&quot;
                </p>
                <span className="testimonial-author">
                  Sarah M., Burnaby Resident
                </span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-project">
                <img src={WORK_SHOTS[1]} alt="Project thumbnail" />
              </div>
              <div className="testimonial-content">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="quote-icon"
                >
                  <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2zM5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path>
                </svg>
                <p className="section-content">
                  &quot;Professional, reliable, and affordable. Our strata
                  building looks brand new after their exterior refresh.&quot;
                </p>
                <span className="testimonial-author">
                  Strata Manager, V5J 0H7
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="quote-cta" className="quote-cta">
        <div className="quote-cta-container">
          <div className="quote-cta-split">
            <div className="quote-cta-text">
              <h2 className="section-title">
                Ready for Your Own Transformation?
              </h2>
              <p className="section-content">
                Inspired by our portfolio? Get a free, no-obligation estimate
                for your next painting or renovation project today.
              </p>
              <ul className="quote-benefits">
                <li className="benefit-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  <span>Reliable &amp; Professional Team</span>
                </li>
                <li className="benefit-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  <span>Affordable Pricing &amp; Premium Quality</span>
                </li>
              </ul>
              <div className="contact-footer">
                <p className="section-content">
                  Shiny Touch Painting &amp; Renovation Ltd
                </p>
                <p className="section-content">
                  99632 Market Crossing PO, Burnaby, BC
                </p>
              </div>
            </div>
            <div className="quote-cta-action">
              <form
                data-form-id="f74790c0-a760-4f9d-aa01-f87d0bf82d3f"
                className="cta-form"
                onSubmit={handleGalleryCtaSubmit}
              >
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Your Name"
                    required
                    id="gallery-cta-name"
                    name="name"
                    data-form-field-id="gallery-cta-name"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    required
                    id="gallery-cta-phone"
                    name="phone"
                    data-form-field-id="gallery-cta-phone"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    id="gallery-cta-email"
                    name="email"
                    data-form-field-id="gallery-cta-email"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <select
                    required
                    defaultValue=""
                    id="gallery-cta-service"
                    name="service"
                    data-form-field-id="gallery-cta-service"
                    className="form-input"
                  >
                    <option value="" disabled>
                      Select Service
                    </option>
                    <option value="interior-painting">Interior Painting</option>
                    <option value="exterior-painting">Exterior Painting</option>
                    <option value="full-renovation">Full Renovation</option>
                    <option value="strata-maintenance">Strata Maintenance</option>
                  </select>
                </div>
                <div className="form-group">
                  <textarea
                    placeholder="Brief project details…"
                    required
                    rows={3}
                    id="gallery-cta-message"
                    name="message"
                    data-form-field-id="gallery-cta-message"
                    className="form-input"
                  />
                </div>
                {ctaMail.message ? (
                  <p
                    className="section-content"
                    style={{
                      marginBottom: '0.75rem',
                      fontSize: '0.9rem',
                      color:
                        ctaMail.status === 'ok'
                          ? 'var(--color-primary)'
                          : 'var(--color-accent)',
                    }}
                    role="status"
                  >
                    {ctaMail.message}
                  </p>
                ) : null}
                <button
                  type="submit"
                  id="gallery-cta-submit"
                  name="button"
                  data-form-field-id="gallery-cta-submit"
                  className="btn btn-primary btn-xl"
                  disabled={ctaMail.status === 'sending'}
                >
                  {ctaMail.status === 'sending'
                    ? 'Sending…'
                    : 'Request Free Quote'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <div className="before-after-gallery-container2">
        <div className="before-after-gallery-container3">
          <InlineScript
            html={`<style>
section {
  padding: var(--spacing-4xl) 0;
  overflow: hidden;
}
</style>`}
          ></InlineScript>
        </div>
      </div>
      <div className="before-after-gallery-container4">
        <div className="before-after-gallery-container5">
          <InlineScript
            html={`<script defer data-name="gallery-interactions">
(function(){
  // Hero Carousel Logic
  const heroSlides = document.querySelectorAll(".gallery-hero-item")
  const heroDots = document.querySelectorAll(".gallery-hero-dot")
  let currentHeroSlide = 0

  function showHeroSlide(index) {
    heroSlides.forEach((slide) => slide.classList.remove("active"))
    heroDots.forEach((dot) => dot.classList.remove("active"))

    heroSlides[index].classList.add("active")
    heroDots[index].classList.add("active")
  }

  heroDots.forEach((dot, index) => {
    dot.addEventListener("click", () => {
      currentHeroSlide = index
      showHeroSlide(currentHeroSlide)
    })
  })

  setInterval(() => {
    currentHeroSlide = (currentHeroSlide + 1) % heroSlides.length
    showHeroSlide(currentHeroSlide)
  }, 5000)

  // Before/After Slider Interaction
  const sliders = document.querySelectorAll(".before-after-container")

  sliders.forEach((container) => {
    const afterImg = container.querySelector(".img-after")
    const sliderLine = container.querySelector(".comparison-slider")

    const moveSlider = (e) => {
      const rect = container.getBoundingClientRect()
      let x = (e.clientX || e.touches[0].clientX) - rect.left
      const width = rect.width

      if (x < 0) x = 0
      if (x > width) x = width

      const percentage = (x / width) * 100
      afterImg.style.clipPath = \`inset(0 0 0 \${percentage}%)\`
      sliderLine.style.left = \`\${percentage}%\`
    }

    container.addEventListener("mousemove", moveSlider)
    container.addEventListener("touchmove", moveSlider)
  })

  // Gallery Filtering
  const filterBtns = document.querySelectorAll(".filter-btn")
  const projectCards = document.querySelectorAll(".project-card")

  filterBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      const filter = btn.getAttribute("data-filter")

      filterBtns.forEach((b) => b.classList.remove("active"))
      btn.classList.add("active")

      projectCards.forEach((card) => {
        if (filter === "all" || card.getAttribute("data-category") === filter) {
          card.style.display = "block"
          card.style.opacity = "0"
          setTimeout(() => (card.style.opacity = "1"), 50)
        } else {
          card.style.display = "none"
        }
      })
    })
  })

})()
</script>`}
          ></InlineScript>
        </div>
      </div>
      <Footer></Footer>
    </div>
  );
}
